<div class="cd">
  <!-- ── Header ── -->
  <div class="cd__header">
    <div class="cd__header-bg"></div>
    <div class="cd__header-inner d-flex align-items-center gap-3">
      <div class="cd__header-icon-wrap">
        <span class="cd__header-emoji">⚔️</span>
      </div>
      <div class="flex-grow-1 min-w-0">
        <div class="cd__header-title">{{ 'CHALLENGES.NEW_CHALLENGE' | translate }}</div>
        <div class="cd__header-sub">Pick your rival. Set the stakes. Prove your worth.</div>
      </div>
      <button mat-icon-button [tabIndex]="-1" type="button" class="cd__close" mat-dialog-close>
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- ── Loading ── -->
  <div
    *ngIf="isCostLoading && !maxPoints"
    class="cd__loader d-flex flex-column align-items-center justify-content-center gap-3"
    [@fadeInOut]
  >
    <div class="cd__loader-ring"></div>
    <span class="cd__loader-label">Loading your points</span>
  </div>

  <!-- ── No points ── -->
  <div *ngIf="!maxPoints && !isCostLoading" class="cd__empty d-flex align-items-start gap-3">
    <mat-icon class="cd__empty-icon">info_outline</mat-icon>
    <div>
      <div class="cd__empty-title">No points available</div>
      <div class="cd__empty-body">{{ 'CHALLENGE_DIALOG.NO_POINTS' | translate }}</div>
    </div>
  </div>

  <!-- ── Main form ── -->
  <ng-template #form>
    <!-- Stepper -->
    <div class="cd__stepper">
      <div class="cd__stepper-steps">
        <ng-container *ngFor="let label of stepLabels; let i = index">
          <div class="cd__stepper-step" [class.is-active]="currentStep === i + 1" [class.is-done]="currentStep > i + 1">
            <div class="cd__stepper-node">
              <span *ngIf="currentStep <= i + 1">{{ i + 1 }}</span>
              <mat-icon *ngIf="currentStep > i + 1" class="cd__stepper-check">check</mat-icon>
            </div>
            <span class="cd__stepper-label-text">{{ label }}</span>
          </div>
          <div *ngIf="i < stepLabels.length - 1" class="cd__stepper-line" [class.is-done]="currentStep > i + 1"></div>
        </ng-container>
      </div>
    </div>

    <div class="cd__body" mat-dialog-content>
      <!-- ── Step 1: Pick Opponent ── -->
      <div *ngIf="currentStep === 1" [@slideIn]>
        <div class="cd__step-hero">
          <span class="cd__step-hero-emoji">🎯</span>
          <div>
            <div class="cd__step-hero-title">Who do you want to challenge?</div>
            <div class="cd__step-hero-sub">Choose your opponent wisely — the battle begins here.</div>
          </div>
        </div>

        <form [formGroup]="opponentFormGroup" class="mt-3">
          <app-agent-input-autocomplete
            (selectionChange)="opponentForm.opponent.setValue($event)"
            [required]="true"
            [placeholder]="'CHALLENGE_DIALOG.OPPONENT_STEP.PLACEHOLDER' | translate"
            [placeholderLabel]="'CHALLENGE_DIALOG.OPPONENT_STEP.INPUT_PLACEHOLDER' | translate"
            [errorLabel]="'CHALLENGE_DIALOG.OPPONENT_STEP.ERROR' | translate"
          ></app-agent-input-autocomplete>
        </form>

        <!-- VS Matchup -->
        <div class="cd__matchup mt-3">
          <div class="cd__matchup-side cd__matchup-side--left">
            <div class="cd__matchup-avatar-wrap">
              <div class="cd__matchup-avatar-ring cd__matchup-avatar-ring--you"></div>
              <div class="cd__matchup-avatar">
                <app-s3-image
                  [image]="authHelper.userProfile?.userData?.avatarUrl || authHelper.userProfile?.avatarUrl"
                  [avatarName]="authHelper.userProfile?.userData?.username || authHelper.userProfile?.username"
                ></app-s3-image>
              </div>
            </div>
            <span class="cd__matchup-name">{{
              authHelper.userProfile?.userData?.nickname || authHelper.userProfile?.username
            }}</span>
            <span class="cd__matchup-tag cd__matchup-tag--you">🔥 You</span>
          </div>

          <div class="cd__matchup-vs">
            <div class="cd__matchup-vs-ring">
              <span class="cd__matchup-vs-text">VS</span>
            </div>
          </div>

          <div class="cd__matchup-side cd__matchup-side--right">
            <div class="cd__matchup-avatar-wrap">
              <div
                class="cd__matchup-avatar-ring cd__matchup-avatar-ring--opponent"
                [class.is-active]="opponentForm.opponent.value"
              ></div>
              <div class="cd__matchup-avatar cd__matchup-avatar--opponent">
                <app-s3-image
                  *ngIf="opponentForm.opponent.value"
                  [image]="opponentForm.opponent.value?.userData?.avatarUrl || opponentForm.opponent.value?.avatarUrl"
                  [avatarName]="
                    opponentForm.opponent.value?.userData?.username || opponentForm.opponent.value?.username
                  "
                ></app-s3-image>
                <mat-icon *ngIf="!opponentForm.opponent.value" class="cd__matchup-placeholder-icon"
                  >person_outline</mat-icon
                >
              </div>
            </div>
            <span class="cd__matchup-name">
              {{ opponentForm.opponent.value?.userData?.nickname || opponentForm.opponent.value?.username || '???' }}
            </span>
            <span class="cd__matchup-tag cd__matchup-tag--opponent">⚡ Rival</span>
          </div>
        </div>
      </div>

      <!-- ── Step 2: Challenge Type ── -->
      <div *ngIf="currentStep === 2" [@slideIn]>
        <div class="cd__step-hero">
          <span class="cd__step-hero-emoji">🏆</span>
          <div>
            <div class="cd__step-hero-title">Choose your battleground</div>
            <div class="cd__step-hero-sub">What will you compete on? Pick the arena.</div>
          </div>
        </div>

        <form [formGroup]="challengeTypeFormGroup" class="mt-3">
          <mat-radio-group class="cd__type-grid" formControlName="challengeType">
            <label
              class="cd__type-card"
              *ngFor="let type of challengeTypes"
              [class.is-selected]="challengeTypeForm.challengeType.value === type"
              [attr.data-type]="type"
            >
              <mat-radio-button [value]="type" class="cd__radio-btn"></mat-radio-button>
              <div class="cd__type-card-icon">{{ challengeTypeMeta[type].emoji }}</div>
              <div class="cd__type-card-body">
                <div class="cd__type-card-name">{{ challengeTypeMeta[type].label }}</div>
                <div class="cd__type-card-desc">{{ challengeTypeMeta[type].desc }}</div>
              </div>
              <div class="cd__type-card-check">
                <mat-icon>check_circle</mat-icon>
              </div>
            </label>
          </mat-radio-group>
        </form>

        <form
          *ngIf="challengeTypeForm.challengeType.value === 'FIVE_STARS_TO_DATE'"
          [formGroup]="amountFormGroup"
          class="mt-3"
        >
          <mat-form-field class="w-100 cd__field">
            <mat-label>{{ 'CHALLENGE_DIALOG.AMOUNT_STEP.TEXT_PLACEHOLDER' | translate }}</mat-label>
            <input type="number" min="1" matInput formControlName="amount" required />
          </mat-form-field>
        </form>
      </div>

      <!-- ── Step 3: Wager ── -->
      <div *ngIf="currentStep === 3" [@slideIn]>
        <div class="cd__step-hero">
          <span class="cd__step-hero-emoji">💰</span>
          <div>
            <div class="cd__step-hero-title">Place your wager</div>
            <div class="cd__step-hero-sub">How many points are you willing to put on the line?</div>
          </div>
        </div>

        <div class="cd__wager mt-3">
          <div class="cd__wager-display">
            <span class="cd__wager-amount">{{ costForm.points.value || 0 }}</span>
            <span class="cd__wager-unit">pts</span>
          </div>
          <div class="cd__wager-balance">
            <mat-icon>account_balance_wallet</mat-icon>
            <span
              >Balance: <strong>{{ maxPoints }}</strong></span
            >
          </div>
          <form [formGroup]="costFormGroup" class="w-100">
            <input
              type="range"
              class="cd__wager-slider"
              min="1"
              [max]="maxPoints"
              formControlName="points"
              [style.--pct]="((costForm.points.value - 1) / (maxPoints - 1)) * 100 + '%'"
            />
          </form>
          <div class="cd__wager-range-labels">
            <span>1</span><span>{{ maxPoints }}</span>
          </div>
          <div class="cd__wager-risk" [attr.data-risk]="wagerRisk">
            <mat-icon>{{ wagerRiskIcon }}</mat-icon>
            {{ wagerRiskLabel }}
          </div>
        </div>
      </div>

      <!-- ── Step 4: Period ── -->
      <div *ngIf="currentStep === 4" [@slideIn]>
        <div class="cd__step-hero">
          <span class="cd__step-hero-emoji">📅</span>
          <div>
            <div class="cd__step-hero-title">Set the clock</div>
            <div class="cd__step-hero-sub">When does the battle begin and end?</div>
          </div>
        </div>

        <form [formGroup]="datesFormGroup" class="mt-3">
          <mat-form-field class="w-100 cd__field" (click)="picker.open()">
            <mat-label>{{ 'CHALLENGE_DIALOG.DATES_STEP.LABEL' | translate }}</mat-label>
            <input
              matInput
              [min]="getMinDate(1) | date: 'yyyy-MM-dd'"
              [satDatepicker]="picker"
              [required]="true"
              formControlName="date"
            />
            <sat-datepicker #picker [rangeMode]="true" [selectFirstDateOnClose]="true"></sat-datepicker>
            <sat-datepicker-toggle matSuffix [for]="picker"></sat-datepicker-toggle>
          </mat-form-field>
        </form>

        <!-- Summary card -->
        <div class="cd__summary" *ngIf="datesForm.date.value?.begin">
          <div class="cd__summary-title">⚔️ Challenge Summary</div>
          <div class="cd__summary-row">
            <span class="cd__summary-label">Rival</span>
            <span class="cd__summary-value">{{
              opponentForm.opponent.value?.userData?.nickname || opponentForm.opponent.value?.username
            }}</span>
          </div>
          <div class="cd__summary-row">
            <span class="cd__summary-label">Type</span>
            <span class="cd__summary-value"
              >{{ challengeTypeMeta[challengeTypeForm.challengeType.value]?.emoji }}
              {{ challengeTypeMeta[challengeTypeForm.challengeType.value]?.label }}</span
            >
          </div>
          <div class="cd__summary-row">
            <span class="cd__summary-label">Wager</span>
            <span class="cd__summary-value cd__summary-value--points">{{ costForm.points.value }} pts</span>
          </div>
        </div>
      </div>
    </div>
    <!-- /.cd__body -->

    <!-- ── Footer ── -->
    <div class="cd__footer d-flex align-items-center justify-content-between gap-2">
      <button class="cd__btn cd__btn--ghost" mat-dialog-close>Cancel</button>
      <div class="d-flex gap-2">
        <button *ngIf="currentStep > 1" class="cd__btn cd__btn--ghost" (click)="prevStep()">
          <mat-icon class="cd__btn-icon">arrow_back</mat-icon> Back
        </button>
        <button *ngIf="currentStep < 4" class="cd__btn cd__btn--next" [disabled]="!canProceed()" (click)="nextStep()">
          Next <mat-icon class="cd__btn-icon">arrow_forward</mat-icon>
        </button>
        <button
          *ngIf="currentStep === 4"
          class="cd__btn cd__btn--fire"
          [disabled]="!canProceed()"
          (click)="onAddChallenge()"
        >
          <span>🔥</span> {{ 'BUTTON.CHALLENGE' | translate }}
        </button>
      </div>
    </div>
  </ng-template>

  <div *ngIf="!maxPoints && !isCostLoading; else form"></div>
</div>
